// The dark gradient hero that anchors every page. See README.md §3.1.
// Contains breadcrumb + mode pill + chips + iconTile + title + subtitle,
// inline Astro mascot, optional rightSlot button, optional progress bar,
// and an optional children slot for the TelemetryRow.

import type { CSSProperties, ReactNode } from "react";
import { InlineAstro, type AstroPose } from "@/components/hero/InlineAstro";

export interface HeroChip {
  label: string;
  bg?: string;
  color?: string;
  dot?: string;
  pulse?: boolean;
}

export interface MissionControlHeroProps {
  breadcrumb?: ReactNode;
  modePill?: string;
  title: string;
  subtitle?: string;
  iconSrc?: string;
  iconGlyph?: ReactNode;
  chips?: HeroChip[];
  rightSlot?: ReactNode;
  astro?: { pose?: AstroPose; message?: string; size?: number };
  progress?: number;
  children?: ReactNode;
}

const SHELL: CSSProperties = {
  position: "relative",
  padding: 22,
  borderRadius: 16,
  background:
    "linear-gradient(135deg, #032d60 0%, #014486 55%, #1558a0 100%)",
  color: "white",
  overflow: "visible",
  boxShadow: "0 8px 32px -12px rgba(3,45,96,.35)",
};

export function MissionControlHero({
  breadcrumb,
  modePill,
  title,
  subtitle,
  iconSrc,
  iconGlyph,
  chips = [],
  rightSlot,
  astro,
  progress,
  children,
}: MissionControlHeroProps): JSX.Element {
  return (
    <div style={SHELL}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          borderRadius: 16,
          pointerEvents: "none",
        }}
      >
        <img
          src="/assets/spark-lavender.png"
          alt=""
          style={{
            position: "absolute",
            top: -20,
            right: -16,
            width: 120,
            opacity: 0.18,
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 20,
          position: "relative",
          zIndex: 2,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
              flexWrap: "wrap",
            }}
          >
            {breadcrumb && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: ".1em",
                  textTransform: "uppercase",
                  color: "#AACBE2",
                  whiteSpace: "nowrap",
                }}
              >
                {breadcrumb}
              </span>
            )}
            {modePill && (
              <span
                style={{
                  fontSize: 10,
                  padding: "2px 9px",
                  borderRadius: 999,
                  background: "rgba(255,255,255,.14)",
                  color: "#C9E3F6",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {modePill}
              </span>
            )}
            {chips.map((c, i) => (
              <span
                key={i}
                style={{
                  fontSize: 10,
                  padding: "2px 9px",
                  borderRadius: 999,
                  background: c.bg || "rgba(255,255,255,.12)",
                  color: c.color || "#C9E3F6",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  whiteSpace: "nowrap",
                }}
              >
                {c.dot && (
                  <span
                    className={c.pulse ? "pulse-dot" : ""}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: c.dot,
                    }}
                  />
                )}
                {c.label}
              </span>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {(iconSrc || iconGlyph) && (
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 10,
                  background: "rgba(255,255,255,.12)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid rgba(255,255,255,.18)",
                  fontSize: 20,
                }}
              >
                {iconSrc ? (
                  <img
                    src={iconSrc}
                    style={{ height: 28, width: 28, objectFit: "contain" }}
                    alt=""
                    onError={(e) => {
                      // 2026-05-03: shared MissionControlHero is reused on
                      // RunDetail / Run history. iconSrc resolves to
                      // matchedPack?.logo which can be an external URL
                      // that 404s. Fall back to the bundled Tableau mark
                      // rather than rendering the broken-image glyph in
                      // the hero band — same pattern as Configure's hero
                      // (Configure.tsx:543) and PackTile (~line 1140).
                      const img = e.currentTarget;
                      if (img.dataset.fallbackApplied) return;
                      img.dataset.fallbackApplied = "1";
                      img.src = "/assets/tableau-logo.png";
                    }}
                  />
                ) : (
                  iconGlyph
                )}
              </div>
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </div>
              {subtitle && (
                <div style={{ fontSize: 12, color: "#AACBE2" }}>
                  {subtitle}
                </div>
              )}
            </div>
          </div>
        </div>
        {(astro || rightSlot) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexShrink: 0,
            }}
          >
            {astro && <InlineAstro {...astro} />}
            {rightSlot}
          </div>
        )}
      </div>

      {typeof progress === "number" && (
        <div
          style={{
            height: 6,
            borderRadius: 999,
            marginTop: 18,
            background: "rgba(255,255,255,.12)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              height: "100%",
              background: "linear-gradient(90deg, #4aa3e3, #6fc4f5)",
              borderRadius: 999,
              transition: "width .4s ease",
              boxShadow: "0 0 12px rgba(111,196,245,.5)",
            }}
          />
        </div>
      )}

      {children && <div style={{ marginTop: 18 }}>{children}</div>}
    </div>
  );
}
